import { useEffect, useRef, useState } from 'react';
import { createToolbarActions, defaultToolbarLabels, renderMarkdown, updatePost, type PostItem } from '@suangua/shared';
import { Avatar } from '../ui/atoms';

/**
 * 帖子详情页弹层（原型同构）：
 * - ReplyModal：回复帖子 / 楼中楼回复（Markdown 工具栏 + 字数统计）
 * - ReportModal：举报内容 / 头像（原因选择 + 补充说明）
 * - AvatarSheet：他人头像操作（查看主页 / 举报头像）
 * - EditPostModal：作者编辑帖子（标题 + Markdown 正文，保存后展示「编辑于」）
 */

/** Markdown 工具栏动作表（与发帖编辑器同源） */
const TOOLBAR_ACTIONS = createToolbarActions(defaultToolbarLabels);

/** 应用工具栏动作：基于光标选区做文本变换 */
function applyToolbarAction(
  action: (typeof TOOLBAR_ACTIONS)[number],
  value: string,
  el: HTMLTextAreaElement | null,
  setValue: (v: string) => void,
) {
  if (!el) return;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? start;
  const { text, sel } = action.transform(value, start, end);
  setValue(text);
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(sel[0], sel[1]);
  });
}

/** 回复目标：null = 回复帖子本身 */
export interface ReplyTarget {
  /** 楼层根评论 id（楼中楼回复时为其 parent_id） */
  rootId: number;
  /** 被回复的子评论 id（回复 L2 时直传，服务端据此写入 reply_user_id 并并入其父楼层） */
  childId?: number;
  /** 被回复人昵称（根作者或子回复作者） */
  toName: string | null;
  /** 根评论内容预览（reply-context 展示） */
  rootPreview: string;
  /** 是否为楼中楼内子回复（@昵称在「头像、昵称」同行展示，不再前缀正文） */
  isChildReply?: boolean;
}

export function ReplyModal(props: {
  open: boolean;
  postTitle: string;
  target: ReplyTarget | null;
  onClose: () => void;
  onSubmit: (content: string) => Promise<void>;
}) {
  const { open, postTitle, target, onClose, onSubmit } = props;
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  // 打开时清空草稿
  useEffect(() => {
    if (open) {
      setDraft('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  async function submit() {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(text);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-wrap modal-md">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-head">
          <p className="modal-title font-zh">{target ? '楼中楼回复' : '回复帖子'}</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* 回复上下文 */}
        {target ? (
          <div className="reply-context">
            <i className="fa-solid fa-corner-down-right text-gold-500" />
            <span>
              回复 <span className="text-gold-500">@{target.toName}</span>
            </span>
            <span className="truncate text-t3">{target.rootPreview.slice(0, 18)}…</span>
          </div>
        ) : (
          <div className="reply-context">
            <i className="fa-solid fa-corner-down-right text-gold-500" />
            <span className="truncate">回复《{postTitle}》</span>
          </div>
        )}

        {/* Markdown 工具栏 */}
        <div className="toolbar mt-3">
          {TOOLBAR_ACTIONS.map((action) => (
            <button
              key={action.key}
              type="button"
              className="tool-btn"
              title={action.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyToolbarAction(action, draft, taRef.current, setDraft)}
            >
              <i className={action.faClass} />
            </button>
          ))}
        </div>

        <textarea
          ref={taRef}
          className="editor-ta mt-2"
          rows={5}
          maxLength={500}
          value={draft}
          placeholder={target ? `回复 @${target.toName}…` : '友善回复，理性讨论…'}
          onChange={(e) => setDraft(e.target.value)}
        />
        <p className="mt-1 text-right text-[11px] text-t3">{draft.length} / 500</p>

        <div className="modal-foot">
          <button className="ghost-btn" onClick={onClose}>
            取消
          </button>
          <button className="gold-btn" disabled={!draft.trim() || submitting} onClick={() => void submit()}>
            <i className="fa-solid fa-paper-plane" /> {submitting ? '发布中…' : '发布'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 举报原因（content = 内容 / avatar = 头像，与原型一致） */
const REPORT_REASONS: Record<'content' | 'avatar', string[]> = {
  content: ['垃圾广告', '人身攻击', '色情低俗', '违法违规', '不实信息', '其他'],
  avatar: ['色情低俗', '血腥暴力', '广告二维码', '政治敏感', '其他'],
};

export interface ReportTarget {
  kind: 'content' | 'avatar';
  /** 被举报对象昵称 */
  targetName: string;
  /** content：帖子 id 或 评论 id（二选一） */
  postId?: number;
  commentId?: number;
  /** avatar：被举报用户 id */
  targetUserId?: number;
}

export function ReportModal(props: {
  open: boolean;
  target: ReportTarget | null;
  onClose: () => void;
  onSubmit: (reason: string, note: string) => Promise<void>;
  onError: (msg: string) => void;
}) {
  const { open, target, onClose, onSubmit, onError } = props;
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 打开时重置
  useEffect(() => {
    if (open) {
      setReason('');
      setNote('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open || !target) return null;
  const reasons = REPORT_REASONS[target.kind] ?? [];

  async function submit() {
    if (!reason) {
      onError('请先选择举报原因');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(reason, note.trim());
      onClose();
    } catch {
      /* 失败提示由调用方 toast，保持弹层打开 */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-wrap modal-sm">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-head">
          <p className="modal-title font-zh">举报{target.kind === 'avatar' ? '头像' : '内容'}</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <p className="text-[12px] text-t2">
          被举报对象：
          <span className="font-zh font-bold text-t1">{target.targetName}</span>
          {target.kind === 'avatar' ? ' 的头像' : ' 发布的内容'}
        </p>

        <div className="mt-3 space-y-1.5">
          {reasons.map((r) => (
            <button
              key={r}
              className={`report-row${reason === r ? ' on' : ''}`}
              onClick={() => setReason(r)}
            >
              <span>{r}</span>
              {reason === r && <i className="fa-solid fa-circle-check ml-auto text-gold-500" />}
            </button>
          ))}
        </div>

        <textarea
          className="editor-ta mt-3"
          rows={2}
          maxLength={200}
          value={note}
          placeholder="补充说明（选填，有助于更快处理）"
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="modal-foot">
          <button className="ghost-btn" onClick={onClose}>
            取消
          </button>
          <button className="danger-btn" disabled={submitting} onClick={() => void submit()}>
            {submitting ? '提交中…' : '提交举报'}
          </button>
        </div>
        <p className="mt-2 text-center text-[10px] text-t3">
          恶意举报将被限制功能 · 处理结果将通过「我的-消息」通知
        </p>
      </div>
    </div>
  );
}

/** 他人头像操作弹层（详情页点头像触发；本人头像不触发，由调用方保证） */
export function AvatarSheet(props: {
  open: boolean;
  name: string;
  url: string | null;
  onClose: () => void;
  onViewProfile: () => void;
  onReportAvatar: () => void;
}) {
  const { open, name, url, onClose, onViewProfile, onReportAvatar } = props;
  if (!open) return null;

  return (
    <div className="modal-wrap modal-sm">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="flex flex-col items-center py-1">
          <Avatar name={name} url={url} size="xl" />
          <p className="mt-2 font-zh text-[15px] font-bold text-t1">{name}</p>
          <p className="mt-0.5 text-[11px] text-t3">点击头像可查看资料或举报头像</p>
        </div>
        <div className="mt-4 space-y-2">
          <button
            className="ghost-btn"
            onClick={() => {
              onClose();
              onViewProfile();
            }}
          >
            <i className="fa-regular fa-user" /> 查看主页
          </button>
          <button
            className="ghost-btn txt-danger"
            onClick={() => {
              onClose();
              onReportAvatar();
            }}
          >
            <i className="fa-regular fa-flag" /> 举报该头像
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 作者编辑帖子弹层：标题（可选）+ Markdown 正文（编辑/预览 + 工具栏）。
 * 保存走 PATCH /api/posts/:id，服务端落 updated_at，详情页展示「编辑于 xx」。
 */
export function EditPostModal(props: {
  open: boolean;
  post: PostItem;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onError: (msg: string) => void;
}) {
  const { open, post, onClose, onSaved, onError } = props;
  const [title, setTitle] = useState('');
  const [draft, setDraft] = useState('');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [submitting, setSubmitting] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  // 打开时以当前帖子内容初始化草稿
  useEffect(() => {
    if (open) {
      setTitle(post.title ?? '');
      setDraft(post.content);
      setTab('edit');
      setSubmitting(false);
    }
  }, [open, post.title, post.content]);

  if (!open) return null;

  async function submit() {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      await updatePost(post.id, { title: title.trim() || null, content: text });
      await onSaved();
      onClose();
    } catch (e) {
      onError(e instanceof Error ? e.message : '保存失败，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-wrap">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-head">
          <p className="modal-title font-zh">编辑帖子</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* 标题（可清空：占卜贴清空后回退自动标题，其余展示「（无标题）」） */}
        <input
          className="field mt-1"
          value={title}
          maxLength={80}
          placeholder={post.board === 'divination' ? '标题（选填，留空自动生成）' : '标题（选填）'}
          onChange={(e) => setTitle(e.target.value)}
        />

        {/* 编辑 / 预览 Tab + 字数 */}
        <div className="mt-3 flex items-center justify-between">
          <div className="seg" style={{ padding: 3, flex: '0 0 auto' }}>
            <button style={{ minHeight: 28, padding: '0 14px' }} className={tab === 'edit' ? 'on' : ''} onClick={() => setTab('edit')}>
              编辑
            </button>
            <button style={{ minHeight: 28, padding: '0 14px' }} className={tab === 'preview' ? 'on' : ''} onClick={() => setTab('preview')}>
              预览
            </button>
          </div>
          <span className="text-[11px] text-t3">{draft.length} / 50000</span>
        </div>

        {tab === 'edit' ? (
          <>
            <div className="toolbar mt-2">
              {TOOLBAR_ACTIONS.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="tool-btn"
                  title={action.title}
                  aria-label={action.title}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyToolbarAction(action, draft, taRef.current, setDraft)}
                >
                  <i className={action.faClass} />
                </button>
              ))}
            </div>
            <textarea
              ref={taRef}
              className="editor-ta mt-2"
              rows={9}
              maxLength={50000}
              value={draft}
              placeholder="正文支持 Markdown：# 标题、**加粗**、> 引用、`代码`、![图片|300](链接)"
              onChange={(e) => setDraft(e.target.value)}
            />
          </>
        ) : (
          <div
            className="md-body editor-preview mt-2"
            // 内容经 shared renderMarkdown 转义，安全注入
            dangerouslySetInnerHTML={{
              __html: draft.trim() ? renderMarkdown(draft) : '<p style="color:var(--t3)">暂无内容</p>',
            }}
          />
        )}

        <div className="modal-foot">
          <button className="ghost-btn" onClick={onClose}>
            取消
          </button>
          <button className="gold-btn" disabled={!draft.trim() || submitting} onClick={() => void submit()}>
            <i className="fa-solid fa-check" /> {submitting ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
